/**
 * Calques de l'éditeur d'escalade (dans la carte de gestion) : pieds de paroi, parois, voies
 * numérotées dans l'ordre du topo, et le tracé en cours. Un clic sur une paroi ou une voie la choisit.
 */
import { Layer, Marker, Source } from '@vis.gl/react-maplibre'
import { routesAlong, type ClimbingEditor } from './useClimbingEditor'

export function ClimbingEditLayers({ editor }: { editor: ClimbingEditor }) {
  const { crags, cragId, routeId, tool, linked, setCragId, setRouteId, setTab } = editor
  if (!editor.site) return null

  const lines: GeoJSON.FeatureCollection = {
    type: 'FeatureCollection',
    features: crags.flatMap((c) =>
      c.cliff_lines.map((line) => ({
        type: 'Feature' as const,
        properties: { selected: c.id === cragId },
        geometry: { type: 'LineString' as const, coordinates: line.map((p) => [p.lng, p.lat]) },
      })),
    ),
  }
  const draft = tool?.kind === 'line' ? tool.points : []
  const pick = () => !tool && linked

  return (
    <>
      <Source id="g-climb-lines" type="geojson" data={lines}>
        <Layer id="g-climb-lines-halo" type="line" paint={{ 'line-color': '#ffffff', 'line-width': ['case', ['get', 'selected'], 9, 6], 'line-opacity': 0.75 }} />
        <Layer
          id="g-climb-lines"
          type="line"
          paint={{ 'line-color': ['case', ['get', 'selected'], '#d84315', '#5d4037'], 'line-width': ['case', ['get', 'selected'], 5, 3], 'line-dasharray': [1, 0.6] }}
        />
      </Source>
      {draft.length > 0 && (
        <Source id="g-climb-draft" type="geojson" data={{ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: draft.map((p) => [p.lng, p.lat]) } }}>
          <Layer id="g-climb-draft" type="line" paint={{ 'line-color': '#d84315', 'line-width': 4 }} />
        </Source>
      )}
      {draft.map((p, i) => (
        <Marker key={`cd-${i}`} longitude={p.lng} latitude={p.lat}>
          <span className="g-vertex g-vertex--active" />
        </Marker>
      ))}

      {crags.map((c) =>
        c.location ? (
          <Marker
            key={`gc-${c.id}`}
            longitude={c.location.lng}
            latitude={c.location.lat}
            anchor="center"
            style={{ zIndex: c.id === cragId ? 3 : 2 }}
            onClick={(e) => {
              e.originalEvent.stopPropagation()
              if (!pick()) return
              setRouteId(null)
              setCragId(c.id)
              setTab('parois')
            }}
          >
            <span className={`g-climb-crag${c.id === cragId ? ' g-climb-crag--active' : ''}`} title={c.name}>
              <span className="mdi mdi-image-filter-hdr" aria-hidden />
            </span>
          </Marker>
        ) : null,
      )}

      {crags.flatMap((c) =>
        routesAlong(c).map((r, i) =>
          r.location ? (
            <Marker
              key={`gr-${r.id}`}
              longitude={r.location.lng}
              latitude={r.location.lat}
              anchor="center"
              style={{ zIndex: r.id === routeId ? 5 : 4 }}
              onClick={(e) => {
                e.originalEvent.stopPropagation()
                if (!pick()) return
                setCragId(c.id)
                setRouteId(r.id)
                setTab('voies')
              }}
            >
              <span
                className={`g-climb-route${r.id === routeId ? ' g-climb-route--active' : cragId != null && c.id !== cragId ? ' g-climb-route--dim' : ''}`}
                title={r.grade ? `${r.name} · ${r.grade}` : r.name}
              >
                {i + 1}
              </span>
            </Marker>
          ) : null,
        ),
      )}
    </>
  )
}
